import React, { useEffect, useState } from 'react';
import { BirdzNotif, BirdzTitle, useNotif } from '@applications-terrains/birdz-react-library';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField
} from '@mui/material';
import axios from 'axios';
import { Form, Formik } from 'formik';
import SelectFieldWrapper from '../../../Tools/Selects/SelectFieldWrapper';
import { useGetExocetFamilies } from '../../../../hooks/datarefs';
import {
  ModalWithLoaderAction,
  RequestStatus
} from '../../../Tools/ModalWithLoader/ModalWithLoader';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

export default function InterventionTypesForm({
  endpoint,
  setRequestStatus,
  action,
  setAction,
  id
}: {
  endpoint: string;
  setRequestStatus: (value: RequestStatus) => void;
  action: ModalWithLoaderAction;
  setAction: (value: ModalWithLoaderAction) => void;
  id: string | null;
}) {
  const { data: families } = useGetExocetFamilies();
  const [values, setValues] = useState<any>();
  const [selectedFamilies, setSelectedFamilies] = useState<any>();
  const [isError, setIsError] = useState<boolean>();
  const [errorWrongId, setErrorWrongId] = useState<boolean>();
  const [noValues, setNoValues] = useState<boolean>();
  const { notif, notifOptions } = useNotif();
  const initialValues = {
    id: null,
    name: null,
    families: [],
    duration: null,
    terrain_configurations: null,
    configuration_required: false
  };

  useEffect(() => {
    if (action === 'edit' && id !== null) {
      axios.get(endpoint + `${id}/`).then((response) => {
        setValues(response.data);
      });
    }
  }, [action, id]);

  useEffect(() => {
    const hasModuleEau = selectedFamilies?.length === 1 && selectedFamilies[0].value === 1;

    const isError =
      selectedFamilies?.length > 1 && selectedFamilies.some((family: any) => family.value === 1);

    if (isError) {
      setIsError(true);
    } else {
      setIsError(false);
    }

    if (hasModuleEau) {
      setIsError(false);
    }
  }, [selectedFamilies, isError]);

  const handleSubmit = async (values: any) => {
    const payload = values;

    if (!id) {
      await axios
        .post(endpoint, payload)
        .then(() => {
          setRequestStatus('success');
          setTimeout(() => {
            setRequestStatus(null);
            setAction(null);
          }, 1500);
        })
        .catch((error) => {
          if (error.response.data.id) {
            setErrorWrongId(true);
          }
          setRequestStatus('error');
          notif({
            content: t('interventionTypesForm.errors.duplicateCode'),
            type: 'error'
          });
        });
    } else {
      await axios
        .put(endpoint + `${id}`, payload)
        .then(() => {
          setRequestStatus('success');
          setTimeout(() => {
            setRequestStatus(null);
            setAction(null);
          }, 1500);
        })
        .catch(() => {
          setRequestStatus('error');
          notif({
            content: t('interventionTypesForm.errors.duplicateCode'),
            type: 'error'
          });
        });
    }
  };

  const { t } = useTranslation();

  const validationSchema = Yup.object().shape({
    id: Yup.string().required(t('interventionTypesForm.errors.idRequired')),
    name: Yup.string().required(t('interventionTypesForm.errors.nameRequired')),
    families: Yup.array()
      .min(1, t('interventionTypesForm.errors.familieMin'))
      .required(t('interventionTypesForm.errors.familieRequired')),
    duration: Yup.number()
      .typeError(t('interventionTypesForm.errors.durationTypeError'))
      .positive(t('interventionTypesForm.errors.durationPositive'))
      .integer(t('interventionTypesForm.errors.durationInteger'))
      .required(t('interventionTypesForm.errors.durationRequired')),
    terrain_configurations: Yup.array()
      .min(1, t('interventionTypesForm.errors.configurationTerrainMin'))
      .required(t('interventionTypesForm.errors.configurationTerrainRequired')),
    configuration_required: Yup.boolean()
  });

  const moduleFamilyTranslations: Record<string, string> = {
    WaterMeter: t('intervention.waterMeter'),
    Repetition: t('intervention.repetitionEquipment'),
    FirePost: t('intervention.firePost'),
    WaterHydrant: t('intervention.waterHydrant'),
    LeakDetection: t('intervention.leakDetection'),
    PAV: t('intervention.pav'),
    LeakPreLocator: t('intervention.leakPreLocator'),
    LuKa: t('intervention.luka'),
    'API-BI': t('intervention.apiBi')
  };

  const moduleFamilyOptions = families
    ? families?.map((family) => ({
        value: family.Id,
        label: moduleFamilyTranslations[family.Name] ?? family.Name
      })) || []
    : [];

  return (
    <>
      <BirdzTitle>{t('fields.interventionType')}</BirdzTitle>
      <Formik
        initialValues={values ? values : initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnChange={true}
        validateOnBlur={true}
        enableReinitialize={true}
      >
        {({ handleChange, values, setFieldValue, errors, touched, resetForm }) => {
          useEffect(() => {
            if (
              !values.id ||
              !values.name ||
              values.families.length === 0 ||
              !values.duration ||
              !values.terrain_configurations ||
              values.terrain_configurations.length === 0
            ) {
              setNoValues(true);
            } else setNoValues(false);
          }, [values]);
          return (
            <Form>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <TextField
                    size="small"
                    type={'text'}
                    name={'id'}
                    value={values.id || ''}
                    fullWidth
                    onChange={(e) => {
                      setErrorWrongId(false);
                      setFieldValue('id', e.target.value);
                    }}
                    label={t('interventionTypesForm.code')}
                    error={touched.id && Boolean(errors.id)}
                    helperText={touched.id && errors.id ? String(errors.id) : ''}
                  />

                  {errorWrongId && (
                    <Box sx={{ mt: 1, color: 'red' }}>
                      {t('interventionTypesForm.errors.duplicateCode')}
                    </Box>
                  )}
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    size="small"
                    type={'text'}
                    name={'name'}
                    value={values.name || ''}
                    fullWidth
                    onChange={(e) => {
                      setFieldValue('name', e.target.value);
                    }}
                    label={t('interventionTypesForm.name')}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name ? String(errors.name) : ''}
                  />
                </Grid>

                <Grid item xs={8}>
                  <SelectFieldWrapper
                    name="families"
                    label={t('interventionTypesForm.families')}
                    options={moduleFamilyOptions}
                    onChange={(_: any, values: any) => {
                      setSelectedFamilies(values);
                      const familyIds = values ? values.map((v: any) => v.value) : [];
                      setFieldValue('families', familyIds);
                    }}
                    isMulti
                  />
                  {isError && (
                    <Alert severity="warning">
                      {t('interventionTypesForm.errors.uniqueWaterMeter')}
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={8}>
                  <TextField
                    size="small"
                    type={'number'}
                    name={'duration'}
                    value={values.duration || ''}
                    fullWidth
                    onChange={(e) => {
                      setFieldValue('duration', e.target.value);
                    }}
                    label={t('interventionTypesForm.duration')}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">minutes</InputAdornment>
                    }}
                    error={touched.duration && Boolean(errors.duration)}
                    helperText={touched.duration && errors.duration ? String(errors.duration) : ''}
                  />
                </Grid>

                <Grid item xs={8}>
                  <SelectFieldWrapper
                    name="terrain_configurations"
                    label={t('interventionTypesForm.terrainConfigurations')}
                    options={[
                      { value: 'EMPTY_PDS', label: t('interventionTypesForm.emptyPds') },
                      {
                        value: 'WATER_METER_WITHOUT_DEVICE',
                        label: t('interventionTypesForm.waterMeterWithoutDevice')
                      },
                      {
                        value: 'WATER_METER_WITH_DEVICE',
                        label: t('interventionTypesForm.waterMeterWithDevice')
                      }
                    ]}
                    isMulti
                  />
                </Grid>

                <Grid item xs={8}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.configuration_required}
                        onChange={handleChange}
                        name="configuration_required"
                      />
                    }
                    label={t('interventionTypesForm.configurationRequired')}
                    labelPlacement="start"
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      ml: 0,
                      width: '100%'
                    }}
                  />
                </Grid>
              </Grid>

              <Grid container justifyContent="center" sx={{ mt: 2 }}>
                <Grid item>
                  <Button
                    sx={{ mr: 2 }}
                    variant="outlined"
                    onClick={() => {
                      resetForm({ values: initialValues });
                      setSelectedFamilies(null);
                      setErrorWrongId(false);
                    }}
                  >
                    {t('interventionTypesForm.reset')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isError || errorWrongId || noValues}
                  >
                    {t('interventionTypesForm.save')}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
      <BirdzNotif options={notifOptions} />
    </>
  );
}
